import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  Link2,
  Target,
  DollarSign,
  TrendingUp,
  Users,
  Sparkles,
  Flame,
  ShoppingCart,
  Film,
  AlertTriangle,
  Rocket,
  Users2,
} from 'lucide-react';
import ParticlesBackground from './ParticlesBackground';

const STEPS = [
  {
    id: 'marca',
    number: 1,
    title: 'Creá una marca',
    description: 'Empezá configurando tu marca en espor.ai.',
    icons: [Building2],
    color: 'indigo',
  },
  {
    id: 'cuentas',
    number: 2,
    title: 'Conectá tus cuentas',
    description: 'Conectá Google Ads, Meta Ads y WhatsApp de forma segura y privada.',
    icons: [Link2],
    color: 'sky',
  },
  {
    id: 'objetivos',
    number: 3,
    title: 'Definí tus objetivos',
    description: 'ROAS, CPA, Inversión, Conversiones. La IA te ayuda a proponer metas realistas.',
    icons: [DollarSign, Target, TrendingUp, Users],
    color: 'emerald',
  },
  {
    id: 'optimizaciones',
    number: 4,
    title: 'Recibí optimizaciones',
    description: 'Sugerencias con IA para mejorar la performance de tus campañas y alcanzar tus objetivos.',
    icons: [Sparkles],
    color: 'amber',
  },
];

const STEP_DURATION = 3000; // 3 segundos entre paso 1, 2 y 3
const OPTIMIZATION_CARD_DURATION = 3500; // tiempo entre cada card que se agrega
const FADE_OUT_DELAY = 2500; // espera antes del fade al terminar
const OPTIMIZATION_COUNT = 4;

const OPTIMIZATIONS = [
  {
    id: 'keywords',
    icon: Flame,
    iconColor: 'text-orange-400',
    description: 'Encontramos 4 términos de búsqueda que te están quemando presupuesto',
    tags: ['Google Ads', 'Keywords'],
  },
  {
    id: 'shopping',
    icon: ShoppingCart,
    iconColor: 'text-gray-300',
    description: 'Separá estos 12 productos en una campaña de Shopping con presupuesto exclusivo',
    tags: ['Shopping', 'Productos'],
  },
  {
    id: 'video',
    icon: Film,
    iconColor: 'text-gray-300',
    description: 'Tu video "UGC - Review" genera atención pero no está convirtiendo',
    tags: ['Meta Ads', 'Video'],
  },
  {
    id: 'alertas',
    icon: AlertTriangle,
    iconColor: 'text-amber-400',
    description: '3 campañas están gastando más de lo esperado sin alcanzar conversiones',
    tags: ['Alertas', 'Presupuesto'],
  },
  {
    id: 'escalar',
    icon: Rocket,
    iconColor: 'text-fuchsia-400',
    description: 'Estas 2 campañas vienen superando tu ROAS objetivo: podés escalar inversión',
    tags: ['ROAS', 'Escalar'],
  },
  {
    id: 'audiencias',
    icon: Users2,
    iconColor: 'text-gray-300',
    description: 'Detectamos solapamiento entre 2 audiencias que compiten entre sí',
    tags: ['Audiencias', 'Meta Ads'],
  },
];

const ObjetivosOptimizacionesAnimation = () => {
  const [step, setStep] = useState(0);
  const [visibleOptCount, setVisibleOptCount] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const currentStep = STEPS[step];
  const isStep4 = step === 3;

  useEffect(() => {
    if (isStep4) {
      setVisibleOptCount(1);
      setIsFadingOut(false);
      let cardCount = 1;
      const cardTimer = setInterval(() => {
        cardCount += 1;
        if (cardCount <= OPTIMIZATION_COUNT) {
          setVisibleOptCount(cardCount);
        }
      }, OPTIMIZATION_CARD_DURATION);
      return () => clearInterval(cardTimer);
    } else {
      const stepTimer = setInterval(() => {
        setStep((s) => (s + 1) % STEPS.length);
      }, STEP_DURATION);
      return () => clearInterval(stepTimer);
    }
  }, [isStep4]);

  useEffect(() => {
    if (isStep4 && visibleOptCount >= OPTIMIZATION_COUNT && !isFadingOut) {
      const fadeTimer = setTimeout(() => {
        setIsFadingOut(true);
      }, FADE_OUT_DELAY);
      return () => clearTimeout(fadeTimer);
    }
  }, [isStep4, visibleOptCount, isFadingOut]);

  useEffect(() => {
    if (isFadingOut) {
      const restartTimer = setTimeout(() => {
        setStep(0);
        setVisibleOptCount(0);
        setIsFadingOut(false);
      }, 700);
      return () => clearTimeout(restartTimer);
    }
  }, [isFadingOut]);


  const colorClasses = {
    indigo: {
      bg: 'bg-white/20',
      border: 'border-white/40',
      icon: 'text-white',
      iconBg: 'bg-white/25',
    },
    sky: {
      bg: 'bg-white/20',
      border: 'border-white/40',
      icon: 'text-white',
      iconBg: 'bg-white/25',
    },
    emerald: {
      bg: 'bg-white/20',
      border: 'border-white/40',
      icon: 'text-white',
      iconBg: 'bg-white/25',
    },
    amber: {
      bg: 'bg-white/20',
      border: 'border-white/40',
      icon: 'text-white',
      iconBg: 'bg-white/25',
    },
  };

  const colors = colorClasses[currentStep.color];

  return (
    <div
      className="absolute inset-0 w-full h-full flex items-center justify-center rounded-2xl overflow-hidden bg-indigo-600"
      aria-hidden="true"
    >
      <div className="absolute inset-0">
        <ParticlesBackground className="absolute inset-0 w-full h-full" />
      </div>

      <div className="relative z-10 w-full max-w-full p-8 flex flex-col h-full max-h-[600px]">
        {/* Título principal - fuera del área de scroll en pasos 1-3 */}
        {!isStep4 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="text-center"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight tracking-tight drop-shadow-sm">
              Optimizá tus campañas
            </h2>
            <p className="text-base sm:text-lg text-indigo-100 font-medium mt-1.5">
              con inteligencia artificial
            </p>
          </motion.div>
        )}

        {!isStep4 && (
          <div className="flex justify-center gap-2 my-4">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={`Paso ${s.number}`}
                onClick={() => setStep(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === step ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {isStep4 ? (
            <motion.div
              key="optimizaciones"
              initial={{ opacity: 0 }}
              animate={isFadingOut ? { opacity: 0, y: 120 } : { opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 120 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              className="flex-1 flex flex-col min-h-0 overflow-hidden"
            >
              {/* Título siempre fijo arriba */}
              <div className="text-center pb-2 flex-shrink-0">
                <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight tracking-tight drop-shadow-sm">
                  Optimizá tus campañas
                </h2>
                <p className="text-base sm:text-lg text-indigo-100 font-medium mt-1.5">
                  con inteligencia artificial
                </p>
              </div>
              {/* Cards se apilan hacia abajo; título permanece fijo arriba */}
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <div className="flex flex-col gap-2">
                  {OPTIMIZATIONS.slice(0, visibleOptCount).map((opt) => {
                    const OptIcon = opt.icon;
                    return (
                      <motion.div
                        key={opt.id}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                        className="w-full rounded-xl bg-indigo-500/30 border border-white/20 p-2.5 flex flex-col h-[72px] overflow-hidden"
                      >
                        <div className="flex items-start gap-3 flex-1 min-h-0">
                          <div className={`flex-shrink-0 p-1.5 rounded-lg bg-white/10 ${opt.iconColor}`}>
                            <OptIcon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                            <p className="text-xs text-indigo-100 leading-relaxed line-clamp-2">
                              {opt.description}
                            </p>
                            <div className="flex flex-wrap gap-1.5 mt-auto pt-1.5 flex-shrink-0">
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-500/40 text-emerald-100 border border-emerald-400/40">
                                espor.ai
                              </span>
                              {opt.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="text-[10px] font-semibold px-2 py-0.5 rounded bg-white/20 text-white border border-white/40"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={currentStep.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="flex-1 flex flex-col items-center justify-center text-center space-y-6"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${colors.bg} ${colors.border} border-2`}
              >
                <span className="text-lg font-bold text-white">{currentStep.number}</span>
              </motion.div>

              <h3 className="text-xl font-bold text-white leading-tight">{currentStep.title}</h3>

              <p className="text-sm text-indigo-100 leading-relaxed max-w-sm">{currentStep.description}</p>

              <div className="flex flex-wrap gap-3 pt-2 justify-center">
                {currentStep.icons.map((Icon, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + i * 0.08 }}
                    className={`flex items-center justify-center rounded-xl ${colors.iconBg} p-3 ${colors.icon}`}
                  >
                    <Icon className="w-6 h-6" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ObjetivosOptimizacionesAnimation;
